import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import {Link} from "react-router-dom";
import {
    InteractionHelper,
    Policy,
    productHelper,
    Quote,
    resourcesHelper,
    Subscription,
    INTERACTION_PROCESS_MTA_ADD_CAR
} from "@falcon/services";
import * as moment from "moment";
import {ListItem} from "@athena-portal/journey-model";
import {lazyInject} from "@athena-portal/core";
import {ILookupService, Types as JourneyCoreTypeDefs} from "@athena-portal/plugin-journey";
import {getAmountWithDecimalDigits} from "../../../../CommonFunctions";
import {
    getAmountToDisplay,
    getInteractionVehicleFuelType,
    getVehicleEngineSize,
    getVehicleNumberOfDoors
} from "@falcon/plugin-falcon-journey";

type Lookup = Array<{ [key: string]: ListItem[] }>;

interface IProps {
    quotes?: Quote[];
    policies?: Policy[];
    isActivePoliciesPresent: boolean;
    subscription?: Subscription;
}

interface IState {
    lookup: Lookup[];
    quoteLimit: number;
    loading: boolean;
}

@authorize
export class YourAnnualQuotesPanel extends React.Component<IProps, IState> {

    private pluginTemplates = new PluginTemplates();
    private readonly ANNUAL_MOTOR_PRODUCT: string = "AnnualMotorInsurance";
    private readonly DATE_FORMAT: string = "D MMM YYYY";

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    private lookupService: ILookupService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            lookup: [],
            quoteLimit: 3,
            loading: false
        }
    }

    public componentDidMount(): void {
        this.fetchLookupValues("vehicleTransmission");
        this.fetchLookupValues("vehicleFuelType");
    }

    public render() {
        if (this.state.loading) {
            return (<Overlay show={true}/>)
        } else {
            return (
                <React.Fragment>
                    {this.renderQuotes()}
                </React.Fragment>
            );
        }
    }

    private renderQuotes(): JSX.Element[] | JSX.Element {
        const annualCarQuotes: any = [];

        // Filter quotes which don't have UW and are not from aggregator.
        const filteredQuotes = this.props.quotes &&
            this.props.quotes.filter(quote => {
                return !(Array.isArray(quote.interaction.underwritingResult) && quote.interaction.underwritingResult.length > 0 &&
                    quote.interaction.source === null)
            });

        if (filteredQuotes && filteredQuotes.length !== 0) {
            filteredQuotes.slice(0, this.state.quoteLimit).forEach(
                quote => {
                    const summary: string = quote.interaction.process === INTERACTION_PROCESS_MTA_ADD_CAR ? "additionalVehicleSummaryPage" : "summary";
                    let process: string = this.props.isActivePoliciesPresent ? quote.interaction.process + "/" + summary : "nb/summary";

                    if (quote.interaction.vehicles && quote.interaction.vehicles.length !== 0) {
                        const annualMotorQuoteLink: string = this.getViewAndEditDetailsLink(this.ANNUAL_MOTOR_PRODUCT, process, quote);
                        this.setQuotesContent(quote, annualCarQuotes, annualMotorQuoteLink);
                    }
                }
            );
            return (
                <div>
                    {(annualCarQuotes && annualCarQuotes.length > 0) && (
                        <div className="journey-content your-annual-quotes-page">
                            <div className="journey-questions">
                                {this.pluginTemplates.renderBackLink("Back", true, this.getBackLink())}
                                {annualCarQuotes}
                                {(filteredQuotes.length > this.state.quoteLimit) && (
                                    <button type="button" className="btn btn-primary btn-next plugin"
                                            onTouchStart={this.loadMore}
                                            onMouseDown={this.loadMore}
                                            id={"show-more"}>
                                        Load more
                                    </button>
                                )}
                                {this.pluginTemplates.renderBackLink("Back", false, this.getBackLink())}
                            </div>
                        </div>
                    )}
                </div>
            )
        } else {
            return (
                <div className="journey-content">
                    <div className="journey-questions">
                        {this.pluginTemplates.renderBackLink("Back", true, this.getBackLink())}
                        <div className="quote quote-summary-panel plugin text-center mb-4 mt-4">
                            <p>You don’t have any active quotes saved</p>
                        </div>
                    </div>
                    {this.pluginTemplates.renderBackLink("Back", false, this.getBackLink())}
                </div>
            )
        }
    }

    private getViewAndEditDetailsLink = (product: string, process: string, quote: Quote): string => {
        return "/journey/show/product/" + product + "/process/" + process + "/" + quote.interaction._id + "?auth";
    }

    private setQuotesContent = (quote: Quote, productQuotes: any[], viewAndEditLink: string): void => {
        const vehicle = quote.interaction.vehicles[0];
        const engineSize: string = getVehicleEngineSize(vehicle);

        productQuotes.push(
            <div
                className={`quote plugin ${resourcesHelper.THEME === "Flow" ? "quote-summary-panel text-center mb-4" : "your-quotes-panel"}`}
                key={quote.interaction._id}>
                {resourcesHelper.THEME === "Flow" && <>
                    <div className="pb-0 mb-3">
                        <img src="/img/annual-quotes.png" className="annual-quote-icon" alt="Annual quote icon"/>
                    </div>
                    <div className="pb-0 mb-3">
                        <div className="mb-2 muli-bold-16">Quote created on</div>
                        <div>{moment(quote.interaction.createOn).format(this.DATE_FORMAT)}</div>
                    </div>
                    {InteractionHelper.isMonthlyQuoteAllowed(quote.interaction) && <div className="pb-0 mb-3">
                        <div className="muli-bold-16-blue mb-2">Monthly <span
                            className="fs-22">£{quote.interaction.factors.monthlyPremium}</span></div>
                        <div>£{quote.interaction.paymentDetails.instalmentPlan.totalAmount} in total,</div>
                        <div>{this.calculatePriceDifference(quote.interaction)} more expensive to pay monthly</div>
                        <div>All figures include interest charge and Insurance Premium Tax</div>
                    </div>}
                    <div className="pb-0 mb-3">
                        <div className="muli-bold-16-blue mb-2">Annually <span
                            className="fs-22">£{quote.interaction.factors.annualPremium}</span></div>
                        <div>All figures include Insurance Premium Tax</div>
                    </div>
                    <div className="pb-0 mb-3">
                        <div className="mb-2 muli-bold-16">Start date</div>
                        <div>{moment(quote.interaction.headerInfo.startDate).format(this.DATE_FORMAT)}</div>
                    </div>
                    <React.Fragment>
                        <div className="pb-0 mb-3">{this.renderCarsAndDrivers(quote)}</div>
                    </React.Fragment>
                    <Link
                        to={viewAndEditLink}
                        className="inner-content"
                        id={"viewAndEditQuote" + quote.quoteId}>
                        View and edit details
                    </Link>
                </>}
                {resourcesHelper.THEME === "Allianz" && <>
                    <div className="h3">{vehicle.make}, {vehicle.model}</div>
                    <div className="number-plate-preview mb-3">{vehicle.registrationNumber}</div>
                    <div className="car-details mb-3">
                        <p className="mb-0">{engineSize ? engineSize + "cc, " : ""}
                            {getVehicleNumberOfDoors(vehicle)} dr,&nbsp;
                            {this.resolveLookupValue("vehicleTransmission", vehicle.transmission)},&nbsp;
                            {this.resolveLookupValue("vehicleFuelType", getInteractionVehicleFuelType(vehicle.fuelType, vehicle.fuelTypeFull))}
                        </p>
                        <p className="mb-0">Manufactured in {vehicle.year}</p>
                    </div>

                    <div id="price" className="price premium-amount">
                        <p className="mb-0 mt-2 small">Price</p>
                        <div
                            className="h3 muli-bold-16 m-0">{getAmountToDisplay(quote.interaction.factors.annualPremium)}</div>
                        <p className="mb-0 mt-2 small"> / year*</p>
                    </div>
                    <div className="mb-3">
                        <div className="mb-2 muli-bold-16">Quote created
                            on: <span>{moment(quote.interaction.createOn).format(this.DATE_FORMAT)}</span></div>
                    </div>
                    {InteractionHelper.isMonthlyQuoteAllowed(quote.interaction) && <div className="mb-3">
                        <p className="muli-bold-16 mb-1">Monthly: <span>{getAmountToDisplay(quote.interaction.factors.monthlyPremium)}</span>
                        </p>
                        <p className="m-0">{getAmountToDisplay(quote.interaction.paymentDetails.instalmentPlan.totalAmount)} in
                            total,</p>
                        <p className="m-0">{this.calculatePriceDifference(quote.interaction)} more expensive to pay
                            monthly</p>
                        <p className="m-0">All figures include interest charge and Insurance Premium Tax</p>
                    </div>}
                    <div className="mb-3">
                        <p className="mb-2 muli-bold-16">Start
                            date: <span>{moment(quote.interaction.headerInfo.startDate).format(this.DATE_FORMAT)}</span>
                        </p>
                    </div>
                    <div className="mt-3">
                        <div className="h3">Drivers</div>
                        {this.getDrivers(vehicle, quote)}
                    </div>
                    <div className={"buttonLinkGrid"}>
                        <Link
                            to={viewAndEditLink}
                            className="inner-content"
                            id={"viewAndEditQuote" + quote.quoteId}>
                            <button type="button" className="btn btn-primary btn-next plugin">View and edit details
                            </button>
                        </Link>
                    </div>
                    <div>
                        <p className="mt-3">* All figures include Insurance Premium Tax</p>
                    </div>

                </>}
            </div>
        );
    }

    private getBackLink(): string {
        let onlySubscriptionProduct: boolean = false;

        if (this.props.policies) {
            onlySubscriptionProduct = productHelper.includesOnlyNonExpireMonthlySubscriptionProduct(this.props.policies);
        }

        if (onlySubscriptionProduct) {
            return "/dashboard/monthlySubscription";
        } else if (this.props.isActivePoliciesPresent && !onlySubscriptionProduct) {
            return "/dashboard/getNewQuote";
        } else {
            return "/dashboard/viewOrGetQuotes";
        }

    }

    private renderCarsAndDrivers(quote: Quote): JSX.Element {
        const vehicles: any = [];

        for (const vehicle of quote.interaction.vehicles) {
            vehicles.push(
                <div className="inner-content" key={vehicle.registrationNumber}>
                    <div
                        className="pb-1">{vehicle.make}, {vehicle.model}{vehicle.registrationNumber && ` (${vehicle.registrationNumber})`}</div>
                    <div
                        className="mb-3">{`${vehicle.year}, ${this.resolveLookupValue("vehicleTransmission", vehicle.transmission)},
                        ${this.resolveLookupValue("vehicleFuelType", vehicle.fuelType)}`}</div>
                    <div className="pb-3">
                        <div className="mb-2 muli-bold-16">Driver(s)</div>
                        {this.getDrivers(vehicle, quote)}
                    </div>
                </div>
            );
        }

        return (
            <div>
                <div className="mb-2 muli-bold-16">Car</div>
                {vehicles}
            </div>
        );
    }

    private getDrivers(vehicle: any, quote: Quote) {
        const drivers: any = [];
        if (vehicle.mainDriverId) {
            drivers.push(
                <div key={vehicle.mainDriverId}>{getDriverInfo(vehicle.mainDriverId)} (main driver)</div>
            );
        }

        for (const driverId of vehicle.driverIds) {
            if (quote.interaction.additionalDrivers && quote.interaction.additionalDrivers.length !== 0
                && driverId !== vehicle.mainDriverId) {
                drivers.push(
                    <div className="mt-3" key={driverId}>
                        {getDriverInfo(driverId)}
                    </div>
                );
            }
        }
        return drivers;

        function getDriverInfo(driverId: any) {
            if (driverId === quote.interaction.policyHolder.personId) {
                return (
                    <span>{quote.interaction.policyHolder.firstName} {quote.interaction.policyHolder.lastName}</span>
                );
            }
            let driverInfo;
            for (const driver of quote.interaction.additionalDrivers) {
                if (driverId === driver.personId) {
                    driverInfo =
                        <span>{driver.firstName} {driver.lastName}</span>
                }
            }
            return driverInfo;
        }
    }

    private fetchLookupValues(lookupName: string) {
        this.lookupService.fetchLookupValues(lookupName, "en-UK").subscribe((lookupValues: ListItem[]) => {
            this.state.lookup[lookupName] = lookupValues;
            this.setState({lookup: this.state.lookup});
        }, () => {
            throw new Error(`Error: failed to fetch lookup values for '${lookupName}' lookup`);
        });
    }

    private resolveLookupValue(lookupName: string, lookupId: string): string {
        if (!lookupId || !this.state.lookup || !this.state.lookup[lookupName]) {
            return "";
        }
        const listItem: ListItem | undefined = this.state.lookup[lookupName].find((listItem: ListItem) => `${listItem.id}` === `${lookupId}`);
        if (!listItem) {
            throw new Error(`Error: no item with id '${lookupId}' found in lookup '${lookupName}'`);
        }
        return listItem.value;
    }

    private loadMore = (): void => {
        this.setState({loading: true})
        this.setState({quoteLimit: this.state.quoteLimit + 3, loading: false})
    }

    private calculatePriceDifference = (interaction: any): string => {
        const amountDifference: number = interaction.paymentDetails.instalmentPlan.totalAmount - interaction.factors.annualPremium;
        return getAmountWithDecimalDigits(amountDifference);
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/quotesPage/YourAnnualQuotesPanel.tsx